// Student Budget Planner - calculation engine & constants (in-browser, GBP)

export const ALL_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const WEEKS_PER_MONTH = 52 / 12;
export const DEFAULT_TERM = { startMonth: 8, length: 9 }; // September, 9-month academic year

export function getTermMonths(term = DEFAULT_TERM) {
  const start = (((term?.startMonth ?? 8) % 12) + 12) % 12;
  const len = Math.max(1, Math.min(12, term?.length ?? 9));
  return Array.from({ length: len }, (_, i) => ALL_MONTHS[(start + i) % 12]);
}

export function defaultPaymentMonths(length) {
  const last = Math.max(0, length - 1);
  return [0, Math.round(last / 2), last];
}

export const fmt = (n) => "£" + Math.round(Number.isFinite(n) ? n : 0).toLocaleString("en-GB");
export const fmt2 = (n) =>
  "£" + (Number.isFinite(n) ? n : 0).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export const LOCATIONS = [
  { id: "home", label: "Living at home", hint: "With family, lower rent" },
  { id: "halls", label: "Student halls", hint: "Often bills included" },
  { id: "shared", label: "Shared house", hint: "Splitting a rented house" },
  { id: "private", label: "Private accommodation", hint: "Renting on your own" },
];

export const ESSENTIALS = {
  bills: {
    label: "Bills",
    icon: "Zap",
    fields: [
      ["electric", "Electric"],
      ["gas", "Gas"],
      ["water", "Water"],
      ["internet", "Internet"],
      ["mobile", "Mobile"],
      ["tvLicence", "TV Licence"],
      ["insurance", "Insurance"],
    ],
  },
  food: {
    label: "Food",
    icon: "Utensils",
    fields: [
      ["groceries", "Groceries"],
      ["takeaways", "Takeaways"],
      ["coffee", "Coffee"],
    ],
  },
  transport: {
    label: "Transport",
    icon: "Bus",
    fields: [
      ["bus", "Bus"],
      ["train", "Train"],
      ["fuel", "Fuel"],
      ["parking", "Parking"],
      ["bike", "Bike"],
    ],
  },
  study: {
    label: "Study Costs",
    icon: "GraduationCap",
    fields: [
      ["books", "Books"],
      ["printing", "Printing"],
      ["equipment", "Equipment"],
      ["software", "Software"],
    ],
  },
  health: {
    label: "Health",
    icon: "HeartPulse",
    fields: [
      ["prescriptions", "Prescriptions"],
      ["gym", "Gym"],
      ["medical", "Medical"],
    ],
  },
};

export const UTILITY_BILLS = ["electric", "gas", "water", "internet"];

export const SOCIAL_FIELDS = [  ["eatingOut", "Eating Out"],
  ["drinks", "Drinks"],
  ["entertainment", "Entertainment"],
  ["streaming", "Streaming"],
  ["shopping", "Shopping"],
  ["hobbies", "Hobbies"],
  ["subscriptions", "Subscriptions"],
];

export const ONEOFF_FIELDS = [
  ["holiday", "Holiday travel home", 3],
  ["christmas", "Christmas", 3],
  ["birthdays", "Birthdays", 0],
  ["freshers", "Freshers", 0],
  ["laptop", "Laptop replacement", 0],
  ["courseTrip", "Course trip", 5],
  ["graduation", "Graduation", 8],
  ["moving", "Moving costs", 0],
  ["deposit", "Deposit", 0],
  ["other", "Other", 0],
];

const blank = (fields) => fields.reduce((a, [k]) => ((a[k] = ""), a), {});

export const defaultState = () => ({
  income: {
    maintenanceLoan: "",
    maintenanceGrant: "",
    bursary: "",
    scholarships: "",
    familySupport: "",
    partTime: "",
    savings: "",
    other: "",
    schedule: "3",
    payments: [
      { amount: "", month: 0 },
      { amount: "", month: 4 },
      { amount: "", month: 8 },
    ],
  },
  term: { ...DEFAULT_TERM },
  living: { location: "shared", rent: "", includesBills: false },
  bills: blank(ESSENTIALS.bills.fields),
  food: blank(ESSENTIALS.food.fields),
  transport: blank(ESSENTIALS.transport.fields),
  study: blank(ESSENTIALS.study.fields),
  health: blank(ESSENTIALS.health.fields),
  social: blank(SOCIAL_FIELDS),
  oneOffs: ONEOFF_FIELDS.reduce((a, [k, , m]) => ((a[k] = { amount: "", month: m }), a), {}),
});

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const sumObj = (obj) => Object.values(obj).reduce((a, v) => a + num(v), 0);

export function calculate(state, whatIf = { rent: 0, food: 0, partTime: 0, extraShift: 0 }) {
  const months = getTermMonths(state.term);
  const M = months.length;
  const inc = state.income;

  const studentFinance =
    num(inc.maintenanceLoan) + num(inc.maintenanceGrant) + num(inc.bursary) + num(inc.scholarships);
  const savings = num(inc.savings);
  const other = num(inc.other);
  const recurringMonthly = num(inc.familySupport) + num(inc.partTime) + whatIf.partTime + whatIf.extraShift;

  const annualIncome = studentFinance + savings + other + recurringMonthly * M;

  const utilitiesInRent = !!state.living.includesBills;
  const billsTotal = Object.entries(state.bills).reduce((sum, [k, v]) => {
    if (utilitiesInRent && UTILITY_BILLS.includes(k)) return sum;
    return sum + num(v);
  }, 0);
  const foodTotal = sumObj(state.food) + whatIf.food;
  const transportTotal = sumObj(state.transport);
  const studyTotal = sumObj(state.study);
  const healthTotal = sumObj(state.health);
  const rentMonthly = num(state.living.rent) + whatIf.rent;

  const livingMonthly = rentMonthly + billsTotal + foodTotal + transportTotal + studyTotal + healthTotal;
  const livingAnnual = livingMonthly * M;

  const socialMonthly = sumObj(state.social);
  const socialAnnual = socialMonthly * M;

  const oneOffMonth = Array(M).fill(0);
  let oneOffTotal = 0;
  Object.values(state.oneOffs).forEach(({ amount, month }) => {
    const a = num(amount);
    oneOffTotal += a;
    const m = Math.min(Math.max(0, month | 0), M - 1);
    oneOffMonth[m] += a;
  });

  const leftover = annualIncome - livingAnnual - socialAnnual - oneOffTotal;

  const safeMonthly = (annualIncome - livingAnnual - oneOffTotal) / M;
  const safeWeekly = safeMonthly / WEEKS_PER_MONTH;

  // Income received per month
  const incomeByMonth = Array(M).fill(recurringMonthly + (savings + other) / M);
  if (inc.schedule === "3") {
    const custom = inc.payments.some((p) => num(p.amount) > 0);
    if (custom) {
      inc.payments.forEach((p) => {
        const m = Math.min(Math.max(0, p.month | 0), M - 1);
        incomeByMonth[m] += num(p.amount);
      });
    } else {
      defaultPaymentMonths(M).forEach((m) => (incomeByMonth[m] += studentFinance / 3));
    }
  } else {
    for (let i = 0; i < M; i++) incomeByMonth[i] += studentFinance / M;
  }

  // Running balance timeline
  const timeline = [];
  let running = 0;
  let firstNegative = -1;
  for (let i = 0; i < M; i++) {
    const spend = livingMonthly + socialMonthly + oneOffMonth[i];
    running += incomeByMonth[i] - spend;
    if (running < 0 && firstNegative === -1) firstNegative = i;
    timeline.push({
      month: months[i],
      balance: Math.round(running),
      income: Math.round(incomeByMonth[i]),
      spend: Math.round(spend),
      payday: incomeByMonth[i] > recurringMonthly + (savings + other) / M + 1,
    });
  }

  // Spending breakdown
  const breakdown = [
    { name: "Rent", value: Math.round(rentMonthly * M) },
    { name: "Bills", value: Math.round(billsTotal * M) },
    { name: "Food", value: Math.round(foodTotal * M) },
    { name: "Transport", value: Math.round(transportTotal * M) },
    { name: "Study", value: Math.round(studyTotal * M) },
    { name: "Health", value: Math.round(healthTotal * M) },
    { name: "Social", value: Math.round(socialAnnual) },
    { name: "One-off", value: Math.round(oneOffTotal) },
  ].filter((d) => d.value > 0);
  const totalSpend = livingAnnual + socialAnnual + oneOffTotal;

  // Status
  let status = "green";
  if (leftover < 0 || safeMonthly <= 0) status = "red";
  else if (leftover < annualIncome * 0.04) status = "amber";

  // Warnings & positives
  const warnings = [];
  const positives = [];
  if (firstNegative >= 0)
    warnings.push(`Your spending is likely to exceed your income by ${months[firstNegative]}.`);
  const rentPct = annualIncome > 0 ? (rentMonthly * M) / annualIncome : 0;
  if (rentPct > 0.4) warnings.push(`Rent is using ${Math.round(rentPct * 100)}% of your income.`);
  if (studyTotal === 0) warnings.push("You have not budgeted for course materials.");
  if (num(state.oneOffs.christmas.amount) === 0)
    warnings.push("Your Christmas spending isn't covered.");
  if (leftover < 0)
    warnings.push(`You're on track to be about ${fmt(Math.abs(leftover))} short this year.`);

  if (leftover >= 0 && firstNegative < 0) {
    positives.push("You're on track.");
    positives.push(
      `You'll finish the year with around ${fmt(leftover)} remaining. Consider moving some into savings for next year.`
    );
  }

  return {
    annualIncome,
    livingAnnual,
    socialAnnual,
    oneOffTotal,
    leftover,
    safeMonthly,
    safeWeekly,
    livingMonthly,
    socialMonthly,
    rentMonthly,
    rentPct,
    timeline,
    breakdown,
    totalSpend,
    status,
    warnings,
    positives,
  };
}

export const PIE_COLORS = ["#734FFC", "#FF8700", "#0F64F8", "#377D30", "#A88AFF", "#FFBF47", "#94BAFE", "#ED0000"];
